import moment from 'moment-timezone';
import _concat from 'lodash/concat';
import _filter from 'lodash/filter';
import _includes from 'lodash/includes';
import _isEmpty from 'lodash/isEmpty';
import _isNil from 'lodash/isNil';
import _lowerCase from 'lodash/lowerCase';
import _map from 'lodash/map';
import _reduce from 'lodash/reduce';

// -- Native dates and duration utils -- //
export function convertDateToUTC(date) {
  return new Date(
    date.getUTCFullYear(),
    date.getUTCMonth(),
    date.getUTCDate(),
    date.getUTCHours(),
    date.getUTCMinutes(),
    date.getUTCSeconds(),
  );
}

export function breakDownDurationInMinutes(durationInMinutes) {
  const MINUTES_PER_DAY = 1440;
  const MINUTES_PER_HOUR = 60;

  const days = Math.floor(durationInMinutes / MINUTES_PER_DAY);
  const hours = Math.floor((durationInMinutes - days * MINUTES_PER_DAY) / MINUTES_PER_HOUR);
  const minutes = durationInMinutes - (days * MINUTES_PER_DAY + hours * MINUTES_PER_HOUR);

  return ({
    days,
    hours,
    minutes,
  });
}

// -- Timezone utils -- //
function internalNamesTimezones() {
  const tzNames = moment.tz.names();

  return _filter(tzNames, (tzName) => (_includes(tzName, '/')));
}

const internalKnownTimezones = [
  'Europe/Paris',
  'Europe/Brussels',
  'Europe/Amsterdam',
  'Europe/Rome',
  'Europe/Zurich',
  'Europe/Madrid',
  'Europe/Lisbon',
  'Europe/Berlin',
];

function internalListTimezones() {
  const allTimezones = _map(internalNamesTimezones(), (tz) => ({
    key: tz,
    label: tz,
    value: tz,
  }));
  return _filter(allTimezones, ({ value }) => (_includes(internalKnownTimezones, value)));
}

function insternalOffsetTimezone(timezone, date) {
  return moment.tz.zone(timezone).utcOffset(date);
}

function insternalOffsetDiffTimezone(timezone1, timezone2) {
  const now = moment();
  return insternalOffsetTimezone(timezone2, now) - insternalOffsetTimezone(timezone1, now);
}

export const timezoneUtils = {
  userTimezone: () => moment.tz.guess(),
  names: () => internalNamesTimezones(),
  list: () => internalListTimezones(),
  offset: (timezone, date) => insternalOffsetTimezone(timezone, date),
  offsetDiff: (timezone1, timezone2) => insternalOffsetDiffTimezone(timezone1, timezone2),
};

// -- Locale global initalizer -- //
export const initLocale = (locale) => {
  moment.locale(locale);
};

// -- Object generator -- //
export const objectGenerator = (string, format) => moment(string, format);

// -- Getters -- //
function internalHoursArrayByDay(date) {
  const datesArray = [];
  for (let i = 0; i < 24; i += 1) {
    datesArray.push(moment(date).set({ hours: i, minutes: 0, seconds: 0 }));
  }
  return datesArray;
}

function internalHoursArrayByDayPredicted(date, now = null) {
  // eslint-disable-next-line no-param-reassign
  now = _isNil(now) ? moment() : moment(now);

  const isToday = now.isSame(moment(date), 'day');

  let start;
  let numberOfHours;

  // Last hour of today: get all next day data
  // Current month: get current month remaining data
  // Another month: get all month data
  if (isToday && now.hour() === 23) {
    start = now.add(1, 'hour');
    numberOfHours = 24;
  } else if (isToday) {
    start = moment(date);
    start = start.isBefore(now) ? now.clone() : start;
    start = start.add(1, 'hour');
    numberOfHours = 23 - start.hour() + 1;
  } else {
    start = moment(date).set({ hour: 0 });
    numberOfHours = 24;
  }

  start = start.set({ minutes: 0, seconds: 0 });

  const datesArray = [];
  for (let i = 0; i < numberOfHours; i += 1) {
    datesArray.push(start.clone().add(i, 'hours'));
  }
  return datesArray;
}

function internalHoursArrayByWeek(date) {
  const daysArray = internalDaysArrayByWeek(date);
  const datesArray = _reduce(daysArray, (result, day) => {
    const dayHours = internalHoursArrayByDay(day);
    return _concat(result, dayHours);
  }, []);
  return datesArray;
}

function internalHoursArrayByWeekPredicted(date) {
  const daysArray = internalDaysArrayByWeekPredicted(date);
  const datesArray = _reduce(daysArray, (result, day) => {
    const dayHours = internalHoursArrayByDayPredicted(day);
    return _concat(result, dayHours);
  }, []);
  return datesArray;
}

function internalHoursArrayByMonth(date) {
  const daysArray = internalDaysArrayByMonth(date);
  const datesArray = _reduce(daysArray, (result, day) => {
    const dayHours = internalHoursArrayByDay(day);
    return _concat(result, dayHours);
  }, []);
  return datesArray;
}

function internalHoursArrayByMonthPredicted(date) {
  const daysArray = internalDaysArrayByMonthPredicted(date);
  const datesArray = _reduce(daysArray, (result, day) => {
    const dayHours = internalHoursArrayByDayPredicted(day);
    return _concat(result, dayHours);
  }, []);
  return datesArray;
}

function internalHoursArrayByDates(begin, end) {
  const daysArray = internalDaysArrayByDates(begin, end);
  const datesArray = _reduce(daysArray, (result, day) => {
    const dayHours = internalHoursArrayByDay(day);
    return _concat(result, dayHours);
  }, []);
  return datesArray;
}

function internalDaysArrayByWeek(date) {
  const firstDayOfWeek = moment(date).startOf('week');
  const datesArray = [];
  for (let i = 0; i < 7; i += 1) {
    datesArray.push(moment(firstDayOfWeek).add({ days: i }).set({ hours: 0, minutes: 0, seconds: 0 }));
  }
  return datesArray;
}

function internalDaysArrayByWeekPredicted(date) {
  const tomorrow = moment(date).add({ days: 1 });
  const datesArray = [];
  for (let i = 0; i <= 7 - tomorrow.day(); i += 1) {
    datesArray.push(moment(tomorrow).add({ days: i }).set({ hours: 0, minutes: 0, seconds: 0 }));
  }
  return datesArray;
}

function internalDaysArrayByQuantity(date, quantity) {
  const initialDate = moment(date).subtract({ days: quantity });
  const datesArray = [];
  for (let i = 0; i < quantity; i += 1) {
    datesArray.push(moment(initialDate).add({ days: i }).set({ hours: 0, minutes: 0, seconds: 0 }));
  }
  return datesArray;
}

function internalDaysArrayByMonth(date) {
  const daysInMonth = moment(date).daysInMonth();
  const datesArray = [];
  for (let i = 0; i < daysInMonth; i += 1) {
    datesArray.push(moment(date).set({
      date: i + 1, hours: 0, minutes: 0, seconds: 0,
    }));
  }
  return datesArray;
}

function internalDaysArrayByMonthPredicted(date, now = null) {
  // eslint-disable-next-line no-param-reassign
  now = _isNil(now) ? moment() : moment(now);

  const isThisMonth = now.isSame(moment(date), 'month');

  let start;
  let numberOfDays;

  // Last day of current month: get all next month data
  // Current month: get current month remaining data
  // Another month: get all month data
  if (isThisMonth && now.daysInMonth() === now.date()) {
    start = now.add(1, 'days');
    numberOfDays = start.daysInMonth();
  } else if (isThisMonth) {
    start = moment(date);
    start = start.isBefore(now) ? now.clone() : start;
    start = start.add(1, 'days');
    numberOfDays = start.daysInMonth() - start.date() + 1;
  } else {
    start = moment(date).set({ date: 1 });
    numberOfDays = start.daysInMonth();
  }

  start = start.set({ hours: 0, minutes: 0, seconds: 0 });

  const datesArray = [];
  for (let i = 0; i < numberOfDays; i += 1) {
    datesArray.push(start.clone().add(i, 'days'));
  }
  return datesArray;
}

function internalDaysArrayByYear(date) {
  const monthsArray = internalMonthsArrayByYear(date);
  const datesArray = _reduce(monthsArray, (result, month) => {
    const monthDays = internalDaysArrayByMonth(month);
    return _concat(result, monthDays);
  }, []);
  return datesArray;
}

function internalDaysArrayByYearPredicted(date) {
  const monthsArray = internalMonthsArrayByYearPredicted(date);
  const datesArray = _reduce(monthsArray, (result, month) => {
    const monthDays = internalDaysArrayByMonthPredicted(month);
    return _concat(result, monthDays);
  }, []);
  return datesArray;
}

function internalDaysArrayByDates(begin, end) {
  const beginDay = moment.utc(begin).startOf('day');
  const endDay = moment.utc(end).startOf('day');
  const daysQuantity = endDay.diff(beginDay, 'days') + 1; // Include begin and end dates
  const datesArray = [];
  for (let i = 0; i < daysQuantity; i += 1) {
    const utcDay = moment.utc(beginDay).add({ days: i });
    datesArray.push(moment({ year: utcDay.year(), month: utcDay.month(), date: utcDay.date() })
      .set({ hours: 0, minutes: 0, seconds: 0 }));
  }
  return datesArray;
}

function internalMonthsArrayByYear(date) {
  const datesArray = [];
  for (let i = 0; i < 12; i += 1) {
    datesArray.push(moment(date).set({ month: i }).set({
      date: 1, hours: 0, minutes: 0, seconds: 0,
    }));
  }
  return datesArray;
}

function internalMonthsArrayByYearPredicted(date, now = null) {
  // eslint-disable-next-line no-param-reassign
  now = _isNil(now) ? moment() : moment(now);

  const isThisYear = now.isSame(moment(date), 'year');

  let start;
  let numberOfMonths;

  // Last month of current year: get all next year data
  // Current year: get current year remaining data
  // Another year: get all year data
  if (isThisYear && now.month() === 11) {
    start = now.add(1, 'months');
    numberOfMonths = 12;
  } else if (isThisYear) {
    start = moment(date);
    start = start.isBefore(now) ? now.clone() : start;
    start = start.add(1, 'months');
    numberOfMonths = 11 - start.month() + 1;
  } else {
    start = moment(date).set({ month: 0, date: 1 });
    numberOfMonths = 12;
  }

  start = start.set({
    date: 1, hours: 0, minutes: 0, seconds: 0,
  });

  const datesArray = [];
  for (let i = 0; i < numberOfMonths; i += 1) {
    datesArray.push(start.clone().add(i, 'months'));
  }
  return datesArray;
}

function internalMonthsArrayByDates(begin, end) {
  const monthsQuantity = internalDiffMonths(end, begin, 'months') + 1; // Include begin and end dates
  const datesArray = [];
  for (let i = 0; i < monthsQuantity; i += 1) {
    datesArray.push(moment(begin).add({ months: i }).set({
      date: 1, hours: 0, minutes: 0, seconds: 0,
    }));
  }
  return datesArray;
}

function internalMonthsArrayByDatesPredicted(begin, end) {
  // For predicted begin the begin it's next month
  const nextMonth = moment(begin).add({ months: 1 }).startOf('month');

  const monthsQuantity = internalDiffMonths(end, nextMonth, 'months') + 1; // Include begin and end dates
  const datesArray = [];
  for (let i = 0; i < monthsQuantity; i += 1) {
    datesArray.push(moment(nextMonth).add({ months: i }).set({
      date: 1, hours: 0, minutes: 0, seconds: 0,
    }));
  }
  return datesArray;
}

export const dateGetter = {
  // https://momentjs.com/docs/#/get-set/
  month: (date) => moment(date).month(),
  dateOfMonth: (date) => moment(date).date(),
  day: (date) => moment(date).days(),
  isoWeekday: (date) => moment(date).isoWeekday(),
  hours: (date) => moment(date).hours(),
  minutes: (date) => moment(date).minutes(),
  seconds: (date) => moment(date).seconds(),
  time: (date) => ({ hour: dateGetter.hours(date), minute: dateGetter.minutes(date), second: dateGetter.seconds(date) }),
  minutesQuantity: (date) => moment(date).hours() * 60 + moment(date).minutes(),
  max: (...dates) => moment.max(_map(dates, (d) => moment(d))),
  min: (...dates) => moment.min(_map(dates, (d) => moment(d))),
  hoursArrayByDay: (date) => internalHoursArrayByDay(date),
  hoursArrayByWeek: (date) => internalHoursArrayByWeek(date),
  hoursArrayByMonth: (date) => internalHoursArrayByMonth(date),
  hoursArrayByDates: (begin, end) => internalHoursArrayByDates(begin, end),
  daysArrayByWeek: (date) => internalDaysArrayByWeek(date),
  daysArrayByQuantity: (date, quantity) => internalDaysArrayByQuantity(date, quantity),
  daysArrayByMonth: (date) => internalDaysArrayByMonth(date),
  daysArrayByYear: (date) => internalDaysArrayByYear(date),
  daysArrayByDates: (begin, end) => internalDaysArrayByDates(begin, end),
  monthsArrayByYear: (date) => internalMonthsArrayByYear(date),
  monthsArrayByDates: (begin, end) => internalMonthsArrayByDates(begin, end),
  endOf: (date, end) => moment(date).endOf(end),
  // Specific for predicted dates
  hoursArrayByDayPredicted: (date, now = null) => internalHoursArrayByDayPredicted(date, now),
  daysArrayByWeekPredicted: (date) => internalDaysArrayByWeekPredicted(date),
  hoursArrayByWeekPredicted: (date) => internalHoursArrayByWeekPredicted(date),
  hoursArrayByMonthPredicted: (date) => internalHoursArrayByMonthPredicted(date),
  daysArrayByMonthPredicted: (date, now = null) => internalDaysArrayByMonthPredicted(date, now),
  daysArrayByYearPredicted: (date) => internalDaysArrayByYearPredicted(date),
  monthsArrayByYearPredicted: (date, now = null) => internalMonthsArrayByYearPredicted(date, now),
  monthsArrayByDatesPredicted: (begin, end) => internalMonthsArrayByDatesPredicted(begin, end),
};

/**
Note concerning below functions:
`updater`, `adder` and `subtractor` are objects with following format (all keys are optional):
for `updater` : {year, month, date, hour(s), minute(s), second(s), millisecond(s)}
for `adder` and `subtractor` : {years, months, weeks, days, hours, minutes, seconds, milliseconds}
/!\ Months are zero indexed, so January is month 0
See https://momentjs.com/docs/#/get-set/set/
*/

// -- Formatters -- //
function internalFormatDate(date, format, updater) {
  if (_isNil(date)) {
    return null;
  }
  const momentDate = moment(date);
  if (!_isEmpty(updater)) {
    momentDate.set(updater);
  }
  return momentDate.format(format);
}

function internalDateToServerFormat(date, timezone, updater) {
  if (_isNil(date)) {
    return null;
  }
  // Apply format to input date to remove timezone information
  // Then, create the date in specified timezone (or use system one)
  const momentDate = moment.tz(moment(date).format('YYYY-MM-DDTHH:mm:ss'), timezone || timezoneUtils.userTimezone());
  if (!_isEmpty(updater)) {
    momentDate.set(updater);
  }
  // Dates are always sent in UTC to server
  momentDate.utc();

  return momentDate.format('YYYY-MM-DDTHH:mm:ssZ');
}

function internalDateToUTC(date, updater) {
  if (_isNil(date)) {
    return null;
  }
  const momentDate = moment(date);
  if (!_isEmpty(updater)) {
    momentDate.set(updater);
  }
  return momentDate.utc().format();
}

function internalDiff(end, begin, measurement) {
  return moment(end).diff(moment(begin), measurement);
}
function internalDiffDays(end, begin) {
  return internalDiff(
    moment(end).set({
      hours: 0, minutes: 0, seconds: 0, milliseconds: 0,
    }),
    moment(begin).set({
      hours: 0, minutes: 0, seconds: 0, milliseconds: 0,
    }),
    'days',
  );
}
function internalDiffMonths(end, begin) {
  return internalDiff(
    moment(end).set({
      date: 1, hours: 0, minutes: 0, seconds: 0, milliseconds: 0,
    }),
    moment(begin).set({
      date: 1, hours: 0, minutes: 0, seconds: 0, milliseconds: 0,
    }),
    'months',
  );
}

function formatFriendlyDate(date) {
  if (_isNil(date)) {
    return null;
  }

  const momentDate = moment(date);
  const now = moment();

  // Calculate the time difference in minutes and days
  const diffMinutes = now.diff(momentDate, 'minutes');
  const diffDays = now.diff(momentDate, 'days');

  switch (true) {
    // Same day, less than an hour ago
    case diffDays === 0 && diffMinutes < 60:
      return {
        id: 'info.connexionMinutesAgo',
        values: { minutes: diffMinutes || 1 }, // Ensure at least 1 minute
      };

    // Same day, hours ago
    case diffDays === 0: {
      const hours = Math.floor(diffMinutes / 60) || 1; // Ensure at least 1 hour
      return {
        id: 'info.connexionHoursAgo',
        values: { hours },
      };
    }

    // Yesterday
    case diffDays === 1:
      return {
        id: 'info.connexionYesterday',
        values: { date: momentDate.format('HH:mm') },
      };

    // Older dates
    default:
      return {
        id: 'info.connexionOlder',
        values: {
          date: momentDate.format('DD/MM/YYYY'),
          time: momentDate.format('HH:mm'),
        },
      };
  }
}

export const dateFormatter = {
  // Server format
  toServerFormat: (date, timezone, updater) => internalDateToServerFormat(date, timezone, updater),
  toServerFormatNoTime: (date, updater) => internalFormatDate(date, 'YYYY-MM-DD', updater),
  toServerFormatTimeOnly: (date, updater) => internalFormatDate(date, 'HH:mm:ss', updater),
  // https://momentjs.com/docs/#/displaying/format/
  toDayOfWeek: (date, updater) => internalFormatDate(date, 'dddd', updater),
  toDayOfMonth: (date, updater) => internalFormatDate(date, 'D', updater),
  toMonthAndYear: (date, updater) => internalFormatDate(date, 'MMMM YYYY', updater),
  toYear: (date, updater) => internalFormatDate(date, 'YYYY', updater),
  toLocalizedDate: (date, updater) => internalFormatDate(date, 'L', updater),
  toLocalizedTime: (date, updater) => internalFormatDate(date, 'LT', updater),
  toUnixTimestamp: (date, updater) => internalFormatDate(date, 'X', updater),
  toUTC: (date, updater) => internalDateToUTC(date, updater),
  // https://momentjs.com/docs/#/displaying/difference/
  diff: (end, begin, measurement) => internalDiff(end, begin, measurement),
  diffDays: (end, begin) => internalDiffDays(end, begin),
  diffMonths: (end, begin) => internalDiffMonths(end, begin),
  formatFriendlyDate: (date) => formatFriendlyDate(date),
};

// -- Date generators -- //
function internalGenerateDate(date, updater) {
  if (_isNil(date)) {
    return null;
  }
  const momentDate = date === 'now' ? moment() : moment(date);
  if (!_isEmpty(updater)) {
    momentDate.set(updater);
  }
  return momentDate.toDate();
}

function internalGenerateNow(updater) {
  return internalGenerateDate('now', updater);
}

function internalGenerateDateFromTimezone(date, timezone) {
  if (_isNil(date)) {
    return null;
  }

  // Convert date to input timezone, then format it to remove timezone information
  const momentDate = moment(moment(date).tz(timezone).format('YYYY-MM-DDTHH:mm:ss'));

  return momentDate.toDate();
}

function internalGenerateDateAdder(date, adder) {
  if (_isNil(date)) {
    return null;
  }
  const momentDate = moment(date);
  if (!_isEmpty(adder)) {
    momentDate.add(adder);
  }
  return momentDate.toDate();
}

function internalGenerateDateSubtractor(date, subtractor) {
  if (_isNil(date)) {
    return null;
  }
  const momentDate = moment(date);
  if (!_isEmpty(subtractor)) {
    momentDate.subtract(subtractor);
  }
  return momentDate.toDate();
}

function getFirstHourAfterNow(date) {
  // Get the current time
  if (_isNil(date)) {
    return null;
  }
  const momentDate = moment(date);

  // Add one hour to the current time
  const nextHour = momentDate.add({ hours: 1 });

  // If the next hour is after the end of the day, set it to the first hour of tomorrow
  if (nextHour.isAfter(moment().endOf('day'))) {
    nextHour.startOf('day').add(1, 'day').add(1, 'hour');
  } else {
    nextHour.startOf('hour');
  }

  // Return the next hour
  return nextHour.toDate();
}

function getFirstDayAfterNow(date) {
  // Get the current time
  if (_isNil(date)) {
    return null;
  }
  const momentDate = moment(date);
  // Add one day to the current time
  const nextDay = (momentDate.add({ days: 1 })).startOf('day');

  return nextDay.toDate();
}

export const dateGenerator = {
  // https://momentjs.com/docs/#/displaying/as-javascript-date/
  now: (updater) => internalGenerateNow(updater),
  date: (date, updater) => internalGenerateDate(date, updater),
  dateFromTimezone: (date, timezone) => internalGenerateDateFromTimezone(date, timezone),
  // https://momentjs.com/docs/#/manipulating/
  dateAdder: (date, adder) => internalGenerateDateAdder(date, adder),
  dateSubtractor: (date, subtractor) => internalGenerateDateSubtractor(date, subtractor),
  dateFirstHourAfterNow: (date) => getFirstHourAfterNow(date),
  dateFirstDayAfterNow: (date) => getFirstDayAfterNow(date),
  nextDay: (date) => internalGenerateDateAdder(date, { days: 1 }),
  nextHour: (date) => internalGenerateDateAdder(date, { hours: 1 }),
  nextMonth: (date) => internalGenerateDateAdder(date, { months: 1 }),
};

// -- Date queries -- //
// https://momentjs.com/docs/#/query/
function internalIsBetween(date, begin, end, granularity, inclusive) {
  const inclusivity = inclusive ? '[]' : '()';
  return moment(date).isBetween(begin, end, granularity, inclusivity);
}

export const dateQuery = {
  isValid: (date) => moment(date).isValid(),
  isBetween: (date, begin, end, granularity = 'minute', inclusive = false) => internalIsBetween(date, begin, end, granularity, inclusive),
  isSame: (date, other, granularity = 'minute') => moment(date).isSame(other, granularity),
  isSameOrBefore: (date, other, granularity = 'minute') => moment(date).isSameOrBefore(other, granularity),
  isSameOrAfter: (date, other, granularity = 'minute') => moment(date).isSameOrAfter(other, granularity),
  isBefore: (date, other, granularity = 'minute') => moment(date).isBefore(other, granularity),
  isAfter: (date, other, granularity = 'minute') => moment(date).isAfter(other, granularity),
  isLastHourOfDay: (date) => moment(date).hour() === 23,
  isLastDayOfMonth: (date) => moment(date).date() === moment(date).daysInMonth(),
  isLastMonthOfYear: (date) => moment(date).month() === 11,
};

// -- Locale data -- //
function internalUsesAmPm() {
  const localTime = _lowerCase(moment().format('LT'));
  // Check if local time format includes 'am' or 'pm'
  return (_includes(localTime, 'am') || _includes(localTime, 'pm'));
}

export const localeData = {
  weekdays: () => moment.weekdays(true),
  firstDayOfWeek: () => moment.localeData().firstDayOfWeek(),
  usesAmPm: () => internalUsesAmPm(),
};

/* New date utils */

export class DateUtils {
  static isDate(value) {
  // eslint-disable-next-line no-restricted-globals
    return value instanceof Date && !isNaN(value);
  }

  static diffDays(date, dateReference = new Date()) {
    const diffTime = dateReference - date;
    const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24));
    return diffDays;
  }

  static formatDate(date, granularity, locale = 'fr-FR') {
    if (granularity === 'DAILY') {
      const options = { day: '2-digit', month: '2-digit', year: 'numeric' };
      const formatter = new Intl.DateTimeFormat(locale, options);
      const day = formatter.format(date).toLowerCase();
      return day;
    }

    if (granularity === 'MONTHLY') {
      const formatter = new Intl.DateTimeFormat(locale, { year: 'numeric', month: 'short' });
      const monthAndYear = formatter.format(date).toLowerCase();
      return monthAndYear;
    }

    if (granularity === 'YEARLY') {
      const year = date.getFullYear().toString();
      return year;
    }
  }

  static formatActivityDate(date) {
    const utcDate = new Date(Date.UTC(date.getFullYear(), date.getMonth(), date.getDate()));
    const year = utcDate.getFullYear();
    const month = String(utcDate.getMonth() + 1).padStart(2, '0');
    const day = String(utcDate.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  }

  static addOrSubstractDate(date, granularity, quantity) {
    const newDate = new Date(date);
    if (granularity === 'DAILY') newDate.setDate(newDate.getDate() + quantity);
    if (granularity === 'MONTHLY') newDate.setMonth(newDate.getMonth() + quantity);
    if (granularity === 'YEARLY') newDate.setFullYear(newDate.getFullYear() + quantity);
    return newDate;
  }

  static firstOfPeriod(date, granularity) {
    const newDate = new Date(date);
    if (granularity === 'DAILY' || granularity === 'MONTHLY' || granularity === 'YEARLY') newDate.setHours(0, 0, 0, 0);
    if (granularity === 'MONTHLY' || granularity === 'YEARLY') newDate.setDate(1);
    if (granularity === 'YEARLY') newDate.setMonth(0);
    return newDate;
  }

  static lastOfPeriod(date, granularity) {
    const newDate = new Date(date);
    if (granularity === 'DAILY' || granularity === 'MONTHLY' || granularity === 'YEARLY') newDate.setHours(23, 59, 59, 999);
    if (granularity === 'MONTHLY') {
      newDate.setMonth(newDate.getMonth() + 1);
      newDate.setDate(0);
    }
    if (granularity === 'YEARLY') {
      newDate.setMonth(11);
      newDate.setDate(31);
    }
    return newDate;
  }

  static lastOfPeriodInUTC(date, granularity) {
    const newDate = new Date(date);
    if (granularity === 'DAILY' || granularity === 'MONTHLY' || granularity === 'YEARLY') newDate.setUTCHours(23, 59, 59, 999);
    if (granularity === 'MONTHLY') {
      newDate.setUTCMonth(newDate.getUTCMonth() + 1);
      newDate.setUTCDate(0);
    }
    if (granularity === 'YEARLY') {
      newDate.setUTCMonth(11);
      newDate.setUTCDate(31);
    }
    return newDate;
  }

  static isSamePeriod(date1, date2, granularity) {
    const isSameYear = date1.getFullYear() === date2.getFullYear();
    const isSameMonth = isSameYear && date1.getMonth() === date2.getMonth();
    const isSameDay = isSameYear && isSameMonth && date1.getDate() === date2.getDate();

    if (granularity === 'YEARLY') return isSameYear;
    if (granularity === 'MONTHLY') return isSameMonth;
    if (granularity === 'DAILY') return isSameDay;

    return false;
  }
}



// WEBPACK FOOTER //
// ./utils/DateUtils.js